//#region src/operator-client-header.ts
/**
* The request header that carries the client label, `<kind>/<version>`.
*
* Who sends it: `FetchCpClient` in `@zion/sdk-node` sends the label its caller sets, such as
* `cli/<version>` or `tui/<version>`; the CP console sends `console/0`. The kind `mcp` is reserved
* for an MCP hop, and no CP code sends it yet.
* Who reads it: the read-audit helper of `@zion/cp-audit-client`, through {@link parseZionClient}.
* Lower case, because Node lower-cases incoming header names.
*/
const ZION_CLIENT_HEADER = "x-zion-client";
/** The client programs a label may name, in the order the pattern lists them. */
const ZION_CLIENT_KINDS = [
	"console",
	"cli",
	"tui",
	"mcp"
];
/**
* The largest header value a label can be, in bytes. {@link ZION_CLIENT_PATTERN} admits ASCII only
* and at most 7 + 1 + 48 = 56 characters, so every value it accepts is within this cap.
*/
const ZION_CLIENT_MAX_BYTES = 64;
/** The longest version part: 1 to 48 characters of `[A-Za-z0-9._-]`. */
const ZION_CLIENT_VERSION_MAX_LENGTH = 48;
/**
* The whole grammar of a label: a kind of {@link ZION_CLIENT_KINDS}, a slash, and a version of 1 to
* {@link ZION_CLIENT_VERSION_MAX_LENGTH} characters from `[A-Za-z0-9._-]`. Built from those two
* constants, anchored at both ends and linear, so it has no backtrack.
*/
const ZION_CLIENT_PATTERN = new RegExp(`^(${ZION_CLIENT_KINDS.join("|")})\\/([A-Za-z0-9._-]{1,48})$`);
/**
* Parse the value of {@link ZION_CLIENT_HEADER}.
*
* Input: any value, usually `req.headers['x-zion-client']`, which Node gives as a string, a string
* array or `undefined`.
* Output:
* - `{ kind, version }` for a string that matches {@link ZION_CLIENT_PATTERN} as a whole, which
*   is at most 56 ASCII characters;
* - `null` for every other input: `undefined`, a non-string, an array (a repeated header), an
*   empty string, an over-long string, an unknown kind, a version with a space or a control
*   character, or text before or after the label.
*
* The anchored pattern stops at the first character outside the grammar or after 56 characters,
* so a long value costs no more than a short one. It never throws, logs nothing and has no side
* effect. A `null` drops the label from the audit row, and the request still runs
* (F3).
*/
function parseZionClient(raw) {
	if (typeof raw !== "string") return null;
	const match = ZION_CLIENT_PATTERN.exec(raw);
	if (match === null) return null;
	return {
		kind: match[1],
		version: match[2]
	};
}
/**
* Format a label for {@link ZION_CLIENT_HEADER}.
*
* Input: a kind and a version. Output: `<kind>/<version>`. It throws a `RangeError` when the
* result does not parse with {@link parseZionClient}, so a client never sends a label the CP drops.
* Who calls it: `FetchCpClient`, once at start with the kind and version its caller set, and the CP
* console with the version `0`, because its build exposes no package version.
*/
function formatZionClient(kind, version) {
	const value = `${kind}/${version}`;
	if (parseZionClient(value) === null) throw new RangeError("the client version must be 1 to 48 characters of [A-Za-z0-9._-]");
	return value;
}
//#endregion
export { ZION_CLIENT_HEADER, ZION_CLIENT_KINDS, ZION_CLIENT_MAX_BYTES, ZION_CLIENT_PATTERN, ZION_CLIENT_VERSION_MAX_LENGTH, formatZionClient, parseZionClient };

//# sourceMappingURL=operator-client-header.js.map